import {
	HttpClient,
	HttpResponse
}                             from '@angular/common/http';
import { Injectable }         from '@angular/core';
import { AbstractApiService } from '@app/core/http/abstract-api.service';
import { SanitizerService }   from '@app/core/http/sanitizer.service';
import { TransitionService }  from '@app/core/http/transition.service';
import {
	IArchivingTssHttpParams,
	IArchivingTssLogHttpParams,
	RequestActionEnum,
	RequestCompleteEntityState,
	RequestCompleteListState
}                             from '@app/core/interfaces/api.interface';
import { ModelTypeEnum }      from '@app/core/models/transition.model';
import { ArchivingTssLog }    from '@app/modules/archiving/models/archiving-tss-log.model';
import { ArchivingTss }       from '@app/modules/archiving/models/archiving-tss.model';
import { LoggerService }      from '@app/shared/services/logger.service';
import {
	ClsTssLogDTO,
	GetTssByBusinessPremisesRequestParams,
	ListTssLogsRequestParams,
	ReadTssLogRequestParams,
	TssLogDTO,
	TssLogsApiClient,
	VirtualTssDTO
}                             from '@openApiGenerated/archiving-api';
import { Observable }         from 'rxjs';

@Injectable( {
	providedIn: 'root'
} )
export class ArchivingTssLogApiService extends AbstractApiService {

	constructor(
		protected http: HttpClient,
		protected logger: LoggerService,
		private readonly tssLogsApiClient: TssLogsApiClient,
		private readonly sanitize: SanitizerService,
		private readonly transition: TransitionService
	) {
		super();
		this.http = http;
	}


	// SANITIZER ######################################
	private sanitizeApiResponse( entity: ArchivingTssLog, context = this ): void {
		if ( entity instanceof ArchivingTssLog ) {
			entity.created = context.sanitize.date<Date>( entity.created, ModelTypeEnum.TMO, Date );
			entity.businessDateFrom = context.sanitize.date<Date>( entity.businessDateFrom, ModelTypeEnum.TMO, Date );
			entity.businessDateTo = context.sanitize.date<Date>( entity.businessDateTo, ModelTypeEnum.TMO, Date );
		}
	}

	private sanitizeTssApiResponse( entity: ArchivingTss, context = this ): void {
		if ( entity instanceof ArchivingTss ) {
			entity.createdDate = context.sanitize.date<Date>( entity.createdDate, ModelTypeEnum.TMO, Date );
			entity.lastModifiedDate = context.sanitize.date<Date>( entity.lastModifiedDate, ModelTypeEnum.TMO, Date );
			entity.activationDate = context.sanitize.date<Date>( entity.activationDate, ModelTypeEnum.TMO, Date );
			entity.deactivationDate = context.sanitize.date<Date>( entity.deactivationDate, ModelTypeEnum.TMO, Date );
		}
	}

	private sanitizeForApiRequest( dto: TssLogDTO, context = this ): void {
		if ( dto instanceof ClsTssLogDTO ) {
		}
	}

	// LIST TYPE REQUESTS #############################
	public search( params: IArchivingTssLogHttpParams, throwError = false ): Observable<RequestCompleteListState<ArchivingTssLog>> {
		this.logger.trace( 'pre-list call' );

		const requestParam: ListTssLogsRequestParams = {
			businessPremisesId: params.businessPremisesId,
			tssId: params?.tssId?.length ? params?.tssId[ 0 ] : null,
			pageSize: params?.paging?.pageSize,
			pageNumber: params?.paging?.pageNumber,
			businessDateFrom: params?.businessDateFrom,
			businessDateTo: params?.businessDateTo,
			sort: params?.sort
		};

		const req$ = this.tssLogsApiClient.listTssLogs( requestParam );
		return this.transition.handleListRequestResponse<TssLogDTO, ArchivingTssLog>(
			req$,
			ArchivingTssLog,
			this,
			this.sanitizeApiResponse,
			null,
			null,
			throwError
		);
	}

	public read( id: string, withFullResponse?: boolean, throwError = false ): Observable<RequestCompleteEntityState<ArchivingTssLog>> {
		this.logger.trace( 'pre-read call: ', id );
		const requestParam: ReadTssLogRequestParams = { tssLogId: id };

		const req$ = this.tssLogsApiClient.readTssLog( requestParam, withFullResponse ? 'response' : undefined );
		return this.transition.handleEntityRequestResponse<TssLogDTO | HttpResponse<TssLogDTO>, ArchivingTssLog>(
			req$,
			RequestActionEnum.READ,
			ArchivingTssLog,
			this,
			this.sanitizeApiResponse,
			null,
			null,
			null,
			throwError
		);
	}

	public searchTss( params: IArchivingTssHttpParams, throwError = false ): Observable<RequestCompleteListState<ArchivingTss>> {
		this.logger.trace( 'pre-list call' );

		const requestParam: GetTssByBusinessPremisesRequestParams = {
			businessPremisesId: params.businessPremisesId,
			search: params?.search,
			pageSize: params?.paging?.pageSize,
			pageNumber: params?.paging?.pageNumber,
			sort: params?.sort
		};

		const req$ = this.tssLogsApiClient.getTssByBusinessPremises( requestParam );
		return this.transition.handleListRequestResponse<VirtualTssDTO, ArchivingTss>(
			req$,
			ArchivingTss,
			this,
			this.sanitizeTssApiResponse,
			null,
			null,
			throwError
		);
	}
}
